import { addDays, weekdayOfCivilDate } from "./time";
import type { DaySlot } from "./tasting";
import { conflictKind, slotLabel, type ConflictKind, type HouseChoice, type SlotDate } from "./venue-conflicts";

/**
 * Free-slot suggestions for the tasting/lead form when a chosen date + slot + house clashes.
 *
 * Same clash rules as the slot-aware warning: a record known to be at the other slot does not
 * clash; a record with no known slot clashes with both slots. On top of that:
 * - "closed" (confirmed/completed event, confirmed booking, active hold) and venue closures
 *   (blocked windows, recurring closed rules) make a slot unavailable: hard clash, never suggested.
 * - "interest" (open leads/tasting cards) is a soft clash: the slot is still suggested, with a count.
 */

export const HOUSE_ORDER: readonly HouseChoice[] = ["casa", "ktema", "regina"];
export const HOUSE_LABELS: Record<HouseChoice, string> = { casa: "CASA", ktema: "ΚΤΗΜΑ", regina: "REGINA" };
const SLOTS: readonly DaySlot[] = ["midday", "evening"];
const WEEKDAYS = ["Κυρ", "Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ"];

/** One record at a house on a date. slot null = not known (blocks/touches both slots). */
export type Occupancy = { venue: HouseChoice; date: string; slot: DaySlot | null; event_id: string; name: string; stage: string };

export type Blocker = { event_id: string; name: string; kind: ConflictKind; slot: DaySlot | null };

export type SlotState = { closed: boolean; hard: Blocker[]; soft: Blocker[] };

export type SuggestionChip = { venue: HouseChoice; date: string; slot: DaySlot | null; soft: number };

export type ClashLevel = "hard" | "soft";

export type RowSuggestions = {
  date: string;
  slot: DaySlot | null;
  level: ClashLevel;
  closed: boolean;
  blockers: Blocker[];
  same_date: SuggestionChip[];
  other_slot: SuggestionChip[];
  nearby: SuggestionChip[];
};

export type SuggestionIndex = {
  slotState(venue: HouseChoice, date: string, slot: DaySlot | null): SlotState;
};

/** Days to look before and after the chosen date for "nearest free date". */
export const NEARBY_WINDOW_DAYS = 70;

/**
 * Indexes occupancies per house/date/event. An event seen twice (event row + tasting card + booking)
 * is merged: closed wins, and its known slots are unioned, as in mergeConflicts.
 */
export function buildSuggestionIndex(occupancies: Occupancy[], closures: Set<string>, excludeEvent: string | null = null): SuggestionIndex {
  type Merged = { event_id: string; name: string; kind: ConflictKind; slots: Set<DaySlot> };
  const byDay = new Map<string, Map<string, Merged>>();
  for (const row of occupancies) {
    if (excludeEvent && row.event_id === excludeEvent) continue;
    const kind = conflictKind(row.stage);
    if (!kind) continue;
    const key = `${row.venue}|${row.date}`;
    const day = byDay.get(key) ?? new Map<string, Merged>();
    byDay.set(key, day);
    const current = day.get(row.event_id);
    if (!current) {
      day.set(row.event_id, { event_id: row.event_id, name: row.name.trim() || "Χωρίς όνομα", kind, slots: new Set(row.slot ? [row.slot] : []) });
      continue;
    }
    if (kind === "closed") current.kind = "closed";
    if (row.slot) current.slots.add(row.slot);
  }
  return {
    slotState(venue, date, slot) {
      const closed = closures.has(`${venue}|${date}`);
      const hard: Blocker[] = [];
      const soft: Blocker[] = [];
      for (const item of byDay.get(`${venue}|${date}`)?.values() ?? []) {
        if (slot && item.slots.size && !item.slots.has(slot)) continue;
        const known = slot && item.slots.has(slot) ? slot : item.slots.size === 1 ? [...item.slots][0] : null;
        const blocker: Blocker = { event_id: item.event_id, name: item.name, kind: item.kind, slot: known };
        (item.kind === "closed" ? hard : soft).push(blocker);
      }
      return { closed, hard, soft };
    },
  };
}

function isFree(state: SlotState): boolean {
  return !state.closed && state.hard.length === 0;
}

/** A slot-less row is free on a day when either slot is free; the chip then names that slot. */
function freeChip(index: SuggestionIndex, venue: HouseChoice, date: string, slot: DaySlot | null): SuggestionChip | null {
  if (slot) {
    const state = index.slotState(venue, date, slot);
    return isFree(state) ? { venue, date, slot, soft: state.soft.length } : null;
  }
  const free = SLOTS.map((candidate) => ({ candidate, state: index.slotState(venue, date, candidate) })).filter((entry) => isFree(entry.state));
  if (!free.length) return null;
  if (free.length === 2) {
    const soft = new Set([...free[0].state.soft, ...free[1].state.soft].map((item) => item.event_id)).size;
    return { venue, date, slot: null, soft };
  }
  return { venue, date, slot: free[0].candidate, soft: free[0].state.soft.length };
}

/** The clash level of one chosen date/slot at one house, or null when nothing clashes. */
export function clashLevel(state: SlotState): ClashLevel | null {
  if (!isFree(state)) return "hard";
  return state.soft.length ? "soft" : null;
}

/**
 * Suggestions for each chosen date that clashes. Groups:
 * a) same date and slot at the other houses that are free;
 * b) the other slot at the same house on the same date, if free;
 * c) the nearest free dates at the same house and slot: same weekday first (2 before, 3 after,
 *    topped up from the other side), then up to 3 other days within ±10 days. Never before today.
 * Clean (no interest) chips come before soft ones inside groups a and b.
 */
export function suggestAlternatives(index: SuggestionIndex, venue: HouseChoice, rows: SlotDate[], today: string): RowSuggestions[] {
  const result: RowSuggestions[] = [];
  for (const row of rows) {
    const state = index.slotState(venue, row.date, row.slot);
    const level = clashLevel(state);
    if (!level) continue;
    const sameDate = HOUSE_ORDER.filter((house) => house !== venue)
      .map((house) => freeChip(index, house, row.date, row.slot))
      .filter((chip): chip is SuggestionChip => chip !== null)
      .sort((left, right) => left.soft - right.soft);
    const otherSlot = SLOTS.filter((slot) => slot !== row.slot)
      .map((slot): SuggestionChip | null => {
        const slotState = index.slotState(venue, row.date, slot);
        return isFree(slotState) ? { venue, date: row.date, slot, soft: slotState.soft.length } : null;
      })
      .filter((chip): chip is SuggestionChip => chip !== null)
      .sort((left, right) => left.soft - right.soft);
    result.push({
      date: row.date,
      slot: row.slot,
      level,
      closed: state.closed,
      blockers: [...state.hard, ...state.soft],
      same_date: row.date >= today ? sameDate : [],
      other_slot: row.date >= today && row.slot ? otherSlot : [],
      nearby: nearbyDates(index, venue, row.date, row.slot, today),
    });
  }
  return result;
}

function nearbyDates(index: SuggestionIndex, venue: HouseChoice, date: string, slot: DaySlot | null, today: string): SuggestionChip[] {
  const weekday = weekdayOfCivilDate(date);
  const before: SuggestionChip[] = [];
  const after: SuggestionChip[] = [];
  for (let week = 1; week * 7 <= NEARBY_WINDOW_DAYS && (before.length < 5 || after.length < 5); week += 1) {
    const earlier = addDays(date, -7 * week);
    const later = addDays(date, 7 * week);
    if (earlier >= today && before.length < 5) {
      const chip = freeChip(index, venue, earlier, slot);
      if (chip) before.push(chip);
    }
    if (after.length < 5) {
      const chip = later >= today ? freeChip(index, venue, later, slot) : null;
      if (chip) after.push(chip);
    }
  }
  let takeBefore = Math.min(2, before.length);
  let takeAfter = Math.min(3, after.length);
  takeAfter = Math.min(after.length, takeAfter + (2 - takeBefore));
  takeBefore = Math.min(before.length, takeBefore + Math.max(0, 3 - takeAfter));
  const weekly = [...before.slice(0, takeBefore).reverse(), ...after.slice(0, takeAfter)];
  const others: SuggestionChip[] = [];
  for (let offset = 1; offset <= 10 && others.length < 3; offset += 1) {
    for (const candidate of [addDays(date, -offset), addDays(date, offset)]) {
      if (others.length >= 3 || candidate < today || weekdayOfCivilDate(candidate) === weekday) continue;
      const chip = freeChip(index, venue, candidate, slot);
      if (chip) others.push(chip);
    }
  }
  return [...weekly, ...others.sort((left, right) => left.date.localeCompare(right.date))];
}

/** "Σάβ 21/11" */
export function weekdayDate(date: string): string {
  const [, month, day] = date.split("-");
  return `${WEEKDAYS[weekdayOfCivilDate(date)]} ${day}/${month}`;
}

/** Chip text, e.g. «REGINA · βράδυ», «μεσημέρι», «Σάβ 21/11 βράδυ». */
export function chipLabel(chip: SuggestionChip, group: "same_date" | "other_slot" | "nearby"): string {
  const slot = slotLabel(chip.slot);
  if (group === "same_date") return [HOUSE_LABELS[chip.venue], slot].filter(Boolean).join(" · ");
  if (group === "other_slot") return slot ?? "";
  return [weekdayDate(chip.date), slot].filter(Boolean).join(" ");
}

/** Athens civil date and slot of an occupancy start (bookings/holds): before 15:00 local is μεσημέρι. */
export function slotOfLocalHour(hour: number): DaySlot {
  return hour < 15 ? "midday" : "evening";
}
